import {
  clearFeatureRecentsBucketsOnce,
  migratePropertyPickerLocalStorageV1ToV2,
  PROPERTY_PICKER_V2_GROUPS_LS_KEY,
  PROPERTY_PICKER_V2_RECENTS_LS_KEY,
} from '@/lib/recoil/property-picker-storage-migration';

export { PROPERTY_PICKER_V2_GROUPS_LS_KEY, PROPERTY_PICKER_V2_RECENTS_LS_KEY };

export const FAVORITE_PROPERTIES_LS_KEY = 'surface_user_favorite_properties';
export const FAVORITE_TENANTS_LS_KEY = 'surface_user_favorite_tenants';
export const TABLE_SETTINGS_KEY = 'surface_table_settings';
export const INVOICE_ITEMS_VIEW_MODE_LS_KEY = 'surface_invoice_items_view_mode';

/**
 * How invoice line items are rendered when drilling into an invoice. 'flat'
 * matches the pre-feature behavior (one row per item). 'byOrder' groups the
 * same items under their owning order with a collapsible header. Persisted
 * per-user via localStorage so the preference survives reloads.
 */
export type InvoiceItemsViewMode = 'flat' | 'byOrder';
const INVOICE_ITEMS_VIEW_MODES: readonly InvoiceItemsViewMode[] = [
  'flat',
  'byOrder',
];

export type TableDensity = 'compact' | 'default' | 'relaxed';

export type TableSettings = {
  density?: TableDensity;
  resultsPerPage?: number;
};

export type ConsolidatedTableSettings = Record<string, TableSettings>;

function canUseLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function readJsonLocalStorage<T>(key: string, fallback: T): T {
  if (!canUseLocalStorage()) return fallback;
  const savedValue = localStorage.getItem(key);
  if (savedValue === null) return fallback;
  try {
    return JSON.parse(savedValue) as T;
  } catch (error) {
    console.error(`Error parsing localStorage value for key ${key}:`, error);
    localStorage.removeItem(key);
    return fallback;
  }
}

export function writeJsonLocalStorage(key: string, value: unknown): void {
  if (!canUseLocalStorage()) return;
  localStorage.setItem(key, JSON.stringify(value));
}

export function removeLocalStorageKey(key: string): void {
  if (!canUseLocalStorage()) return;
  localStorage.removeItem(key);
}

export function loadPropertyPickerRecents<T extends object>(fallback: T): T {
  migratePropertyPickerLocalStorageV1ToV2();
  clearFeatureRecentsBucketsOnce();
  return readJsonLocalStorage(PROPERTY_PICKER_V2_RECENTS_LS_KEY, fallback);
}

export function loadPropertyPickerGroups<T extends object>(fallback: T): T {
  migratePropertyPickerLocalStorageV1ToV2();
  return readJsonLocalStorage(PROPERTY_PICKER_V2_GROUPS_LS_KEY, fallback);
}

export function persistPropertyPickerRecents(value: unknown): void {
  writeJsonLocalStorage(PROPERTY_PICKER_V2_RECENTS_LS_KEY, value);
}

export function persistPropertyPickerGroups(value: unknown): void {
  writeJsonLocalStorage(PROPERTY_PICKER_V2_GROUPS_LS_KEY, value);
}

export function loadFavoriteProperties(): string[] {
  return readJsonLocalStorage<string[]>(FAVORITE_PROPERTIES_LS_KEY, []);
}

export function persistFavoriteProperties(value: string[]): void {
  writeJsonLocalStorage(FAVORITE_PROPERTIES_LS_KEY, value);
}

export function loadFavoriteTenants(): string[] {
  return readJsonLocalStorage<string[]>(FAVORITE_TENANTS_LS_KEY, []);
}

export function persistFavoriteTenants(value: string[]): void {
  writeJsonLocalStorage(FAVORITE_TENANTS_LS_KEY, value);
}

/**
 * Default mode when no preference is persisted yet. Matches the pre-feature
 * behavior so first-visit users see the familiar flat list.
 */
const DEFAULT_INVOICE_ITEMS_VIEW_MODE: InvoiceItemsViewMode = 'flat';

export function loadInvoiceItemsViewMode(): InvoiceItemsViewMode {
  const raw = readJsonLocalStorage<unknown>(
    INVOICE_ITEMS_VIEW_MODE_LS_KEY,
    DEFAULT_INVOICE_ITEMS_VIEW_MODE
  );
  return INVOICE_ITEMS_VIEW_MODES.includes(raw as InvoiceItemsViewMode)
    ? (raw as InvoiceItemsViewMode)
    : DEFAULT_INVOICE_ITEMS_VIEW_MODE;
}

export function persistInvoiceItemsViewMode(value: InvoiceItemsViewMode): void {
  writeJsonLocalStorage(INVOICE_ITEMS_VIEW_MODE_LS_KEY, value);
}

export function readConsolidatedTableSettings(): ConsolidatedTableSettings {
  return readJsonLocalStorage<ConsolidatedTableSettings>(
    TABLE_SETTINGS_KEY,
    {}
  );
}

export function persistTableSetting(
  tableId: string,
  settingKey: 'density' | 'resultsPerPage',
  value: TableDensity | number | undefined,
  isReset: boolean
): void {
  if (!canUseLocalStorage()) return;

  const consolidated = readConsolidatedTableSettings();

  if (isReset) {
    if (consolidated[tableId]) {
      delete consolidated[tableId][settingKey];
      if (Object.keys(consolidated[tableId]).length === 0) {
        delete consolidated[tableId];
      }
    }
  } else {
    if (!consolidated[tableId]) {
      consolidated[tableId] = {};
    }
    if (settingKey === 'density') {
      consolidated[tableId].density = value as TableDensity | undefined;
    } else {
      consolidated[tableId].resultsPerPage = value as number | undefined;
    }
  }

  writeJsonLocalStorage(TABLE_SETTINGS_KEY, consolidated);
}
